// How a composed forecast draws each source: which channels of the base model are shown and every line's colour.
// Stored on the source as `metadata.display` (no schema change), alongside `metadata.accuracy`. Display never
// changes what accuracy scores — hiding a band only stops it being drawn.

import type { SeriesParameter } from "./seriesMetaData";

export interface ChannelDisplay {
  /** false hides the channel; anything else shows it. */
  visible?: boolean;
  /** CSS colour; unset keeps the chart's default. */
  color?: string;
}

export interface SourceDisplaySettings {
  /** Overlay sources: the line colour. */
  color?: string;
  /** Base model: per channel — "mean", "control", "value", "p50", or a band "p25-p75". */
  channels?: Record<string, ChannelDisplay>;
  /** Observation source: colour of the error line on the right axis. */
  errorColor?: string;
}

export interface ChannelOption {
  key: string; // "mean" | "control" | "value" | "p50" | "p25-p75" | "p40" (an unpaired percentile)
  label: string;
  kind: "line" | "band";
  defaultColor: string;
}

/** The chart's own defaults, shown in the colour pickers until the user picks something else. */
export const DEFAULT_CHANNEL_COLORS: Record<string, string> = {
  mean: "#EF5350",
  control: "#2563eb",
  value: "#16a34a",
  p50: "#5e692e",
};
// Outer → inner band shades of the chart's percentile palette
const BAND_SHADES = ["#e0e6c5", "#c2d08f", "#a5b962", "#8ba043"];
export const DEFAULT_ERROR_COLOR = "#7c3aed";

/**
 * The channels a base model can show, from its seriesMetaData parameter: mean / control / single value lines,
 * the median, and symmetric percentile bands (widest first). Members-only forecasts are drawn as percentiles
 * computed from the members (10/25/50/75/90), so those are offered.
 */
export function channelOptions(param: SeriesParameter | null): ChannelOption[] {
  if (!param) return [];
  const { kinds } = param;
  const percentiles =
    kinds.percentiles.length || !kinds.members ? kinds.percentiles : [10, 25, 50, 75, 90];
  const options: ChannelOption[] = [];
  if (kinds.mean) options.push({ key: "mean", label: "Mean", kind: "line", defaultColor: DEFAULT_CHANNEL_COLORS.mean });
  if (kinds.control) options.push({ key: "control", label: "Control", kind: "line", defaultColor: DEFAULT_CHANNEL_COLORS.control });
  if (kinds.value) options.push({ key: "value", label: "Value", kind: "line", defaultColor: DEFAULT_CHANNEL_COLORS.value });
  if (percentiles.includes(50)) options.push({ key: "p50", label: "Median (p50)", kind: "line", defaultColor: DEFAULT_CHANNEL_COLORS.p50 });
  const lows = percentiles.filter((p) => p < 50 && percentiles.includes(100 - p)).sort((a, b) => a - b);
  lows.forEach((p, i) =>
    options.push({
      key: `p${p}-p${100 - p}`,
      label: `p${p}–p${100 - p} band`,
      kind: "band",
      defaultColor: BAND_SHADES[Math.min(i, BAND_SHADES.length - 1)],
    })
  );
  // A percentile without its mirror (e.g. p40 alone) is drawn as a line of its own
  percentiles
    .filter((p) => p !== 50 && !percentiles.includes(100 - p))
    .forEach((p) => options.push({ key: `p${p}`, label: `p${p}`, kind: "line", defaultColor: BAND_SHADES[0] }));
  return options;
}

/** "p25" / "P25" → 25; null when the key isn't a percentile. */
const percentileNumber = (percentileKey: string): number | null => {
  const p = Number(String(percentileKey).replace(/^p/i, ""));
  return Number.isFinite(p) ? p : null;
};

/** The band key a percentile series belongs to: p25 → "p25-p75", p75 → "p25-p75", p50 → null. */
export const bandKeyOf = (percentileKey: string): string | null => {
  const p = percentileNumber(percentileKey);
  if (p === null || p === 50) return null;
  const lo = Math.min(p, 100 - p);
  return `p${lo}-p${100 - lo}`;
};

const isHidden = (display: SourceDisplaySettings | null | undefined, key: string) =>
  display?.channels?.[key]?.visible === false;

/**
 * Apply a base model's display settings to the chart's channel sets: drop hidden channels and work out the
 * colour overrides ProcessedScatterGraph takes (only channels the user coloured — the rest keep its defaults).
 */
export function applyChannelDisplay(
  percentiles: Record<string, unknown>,
  otherChannels: Record<string, unknown>,
  display: SourceDisplaySettings | null | undefined
) {
  const keptPercentiles: Record<string, unknown> = {};
  const present = new Set(Object.keys(percentiles || {}).map(percentileNumber));
  for (const [key, series] of Object.entries(percentiles || {})) {
    // p50 and unpaired percentiles hide on their own key; band edges (both mirrors present) hide with their band
    const p = percentileNumber(key);
    const paired = p !== null && p !== 50 && present.has(100 - p);
    if (isHidden(display, paired ? (bandKeyOf(key) as string) : key.toLowerCase())) continue;
    keptPercentiles[key] = series;
  }
  const keptOther: Record<string, unknown> = {};
  for (const [key, series] of Object.entries(otherChannels || {})) {
    if (!isHidden(display, key)) keptOther[key] = series;
  }

  const channelColors: Record<string, string> = {};
  const bandColors: Record<string, string> = {};
  for (const [key, setting] of Object.entries(display?.channels || {})) {
    if (!setting?.color) continue;
    if (/^p\d+-p\d+$/.test(key)) bandColors[key] = setting.color;
    else channelColors[key] = setting.color;
  }
  return { percentiles: keptPercentiles, otherChannels: keptOther, channelColors, bandColors };
}
